import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BankAccountFormValues } from "../../types/forms";
import { FORM_NAMES } from "../../constants/forms";
import { change, getFormValues, isValid } from "redux-form";
import { RootState } from "../../types/redux";
import i18n from "../../i18n/config";
import { BANK_ACCOUNT_FLOW_TYPES, BANK_ACCOUNT_FORM_FIELDS } from "../../constants/enums";
import { BeneBankHooksProps } from "../../types/hooks";
import { ROUTES } from "../../constants/clientRoutes";
import { useHistory } from "react-router-dom";
import { SaveBankAccountOptionsValues, SetBankAccountBeneBankDetails, SetSelectedCountry, SetSelectedCurrency } from "../../actions/bankAccounts";
import { GetBeneficiaryBankFieldsResponse } from "../../types/api/bankAccounts";
import BankAccountsApi from "../../api/bankAccounts";
import useErrorModal from "../useErrorModal";
import { CountryType } from "../../types/api/catalog";

const useBeneDetailsInit = (props: BeneBankHooksProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const openErrorModal = useErrorModal();
  const formValues = useSelector((state: RootState) => getFormValues(FORM_NAMES.BANK_ACCOUNTS)(state)) as BankAccountFormValues;
  const valid = useSelector((state: RootState) => isValid(FORM_NAMES.BANK_ACCOUNTS)(state));
  const userGuid = useSelector((state: RootState) => state.user.profile.userGuid);
  const searchParams = new URLSearchParams(location.search);
  const { enabled, setTitle, setSubTitle, setForwardButton, setLoading, type, pages } = props;
  const forwardButtonInitialState = { text: i18n.t("COMMON.CONFIRM"), clickFunc: () => history.push(ROUTES.BANKS_ACCOUNTS.BANK_DETAILS), disabled: false };

  /**
   * Set initial title and button values
   */
  const setInitialGlobalValues = () => {
    const title = i18n.t("PAGES.BANK_ACCOUNTS.BENEFICIARY_DETAILS.TITLE");
    const subtitle = type === BANK_ACCOUNT_FLOW_TYPES.NEW ? i18n.t("PAGES.BANK_ACCOUNTS.BENEFICIARY_BANK_DETAILS.ADD_NEW_SUBTITLE") : i18n.t("PAGES.BANK_ACCOUNTS.BENEFICIARY_BANK_DETAILS.EDIT_SUBTITLE");
    setTitle(title);
    setSubTitle(subtitle);
    setForwardButton(forwardButtonInitialState);
  }

  /**
     * Initialize the values of the fields in the form
     * @param response 
     */
  const initializeValues = (response: GetBeneficiaryBankFieldsResponse) => {
    response.result.pages.forEach(page => {
      page.sections.forEach(section => {
        section.fields.forEach(field => {
          dispatch(change(FORM_NAMES.BANK_ACCOUNTS, field.serviceProviderFieldId, field.value));
        })
      })
    })
  }

  /**
 * Save nature of relationship and beneficiary country options to redux for use in the review account details page and submission
 * @param response 
 */
  const saveDataForReviewAccountDetails = (response: GetBeneficiaryBankFieldsResponse) => {
    // Save options to redux
    const natureOfRelationshipField = response.result.pages[0]?.sections[0]?.fields.find(field => field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.NATURE_OF_RELATIONSHIP);
    const beneficiaryCountryField = response.result.pages[0]?.sections[0]?.fields.find(field => field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_COUNTRY);
    const residentialStatusField = response.result.pages[0]?.sections[0]?.fields.find(field => field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.RESIDENTIAL_STATUS);
    const bankAccountType = response.result.pages[0]?.sections[0]?.fields.find(field => field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.BANK_ACCOUNT_TYPE);

    dispatch(SaveBankAccountOptionsValues({
      natureOfRelationshipOptions: natureOfRelationshipField?.options,
      beneficiaryCountryOptions: beneficiaryCountryField?.options,
      residentialStatusOptions: residentialStatusField?.options,
      bankAccountTypeOptions: bankAccountType?.options
    }))
  }

  /**
   * Get the fields for the beneficiary bank details form
   */
  const getFields = async (countryId: number, currencyId: number, allocationId?: string) => {
    try {
      const response = await BankAccountsApi.GetBeneficiaryBankFields(countryId, currencyId, allocationId) as GetBeneficiaryBankFieldsResponse;
      handleActionsFromResponse(response);
    } catch (err) {
      openErrorModal(err);
    }
  }

  /**
   * If user is editing or fixing a bank account, get countryId, currencyId, and allocationId from the URL and fetch the fields
   */
  const handleEditFlow = async () => {
    const countryId = Number(searchParams.get("destinationCountryId"));
    let currencyCode = searchParams.get("destinationCurrencyCode");
    const allocationId = searchParams.get("allocationId");

    try {
      const response = await BankAccountsApi.GetCountriesCurrencies(userGuid) as CountryType[];
      const country = response.find((country: CountryType) => country.id === countryId);
      const currency = country?.currencies?.find(currency => currency.code === currencyCode);

      // The country/currency come from URL query params, so they may not resolve to anything real
      if (!country || !currency) {
        throw new Error(`Unable to resolve destination country "${countryId}" and currency "${currencyCode}"`);
      }

      const currencyId = currency.id;

      // Set redux values for selected country and currency, to use later in PUT request
      dispatch(SetSelectedCountry(country.currencies ?? [], country.name));
      dispatch(SetSelectedCurrency(currency.name, currencyCode ?? ""));

      dispatch(change(FORM_NAMES.BANK_ACCOUNTS, BANK_ACCOUNT_FORM_FIELDS.DESTINATION_COUNTRY, countryId));
      dispatch(change(FORM_NAMES.BANK_ACCOUNTS, BANK_ACCOUNT_FORM_FIELDS.DESTINATION_CURRENCY, currencyId));

      await getFields(countryId, currencyId, allocationId ?? undefined);

    } catch (err) {
      openErrorModal(err);
    }
  }

  const handleActionsFromResponse = (response: GetBeneficiaryBankFieldsResponse) => {
    initializeValues(response);
    saveDataForReviewAccountDetails(response);
    dispatch(SetBankAccountBeneBankDetails(response));
    setLoading(false);
  }

  useEffect(() => {
    if (valid) {
      setForwardButton(forwardButtonInitialState);
    } else {
      setForwardButton({ ...forwardButtonInitialState, disabled: true });
    }
  }, [valid]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setInitialGlobalValues();
    // Fix for case when allocations is MVC and Bank Accounts is React and 
    // user clicks "Fix bank" from Allocations flow which initiates a MVC redirect to this page, but without query params or redux values
    // Rare edge case that can be removed after MVC is removed
    // else continue to fetch sections from API
    if (!type && !formValues?.destinationCountry) {
      history.push(ROUTES.BANKS_ACCOUNTS.INDEX);
    } else if ((pages?.length ?? 0) > 0) {
      setLoading(false);
    } else if (type === BANK_ACCOUNT_FLOW_TYPES.EDIT) {
      handleEditFlow();
    } else {
      getFields(Number(formValues?.destinationCountry), Number(formValues?.destinationCurrency));
    }
  }, [enabled]);
};

export default useBeneDetailsInit;